import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Shield,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Pencil,
  Trash2,
  Loader2,
  Lock,
  Globe,
  Server,
  Mail,
  Key,
  Clock,
  X,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const TYPE_CONFIG = {
  wordpress: { label: "WordPress", icon: Globe, color: "text-[#21759B]", bg: "bg-[#21759B]/10" },
  hebergement: { label: "Hebergement", icon: Server, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
  domaine: { label: "Domaine", icon: Globe, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10" },
  ftp: { label: "FTP", icon: Server, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
  google: { label: "Google", icon: Globe, color: "text-[#4285F4]", bg: "bg-[#4285F4]/10" },
  meta: { label: "Meta", icon: Globe, color: "text-[#0668E1]", bg: "bg-[#0668E1]/10" },
  email: { label: "Email", icon: Mail, color: "text-[#EF4444]", bg: "bg-[#EF4444]/10" },
  autre: { label: "Autre", icon: Key, color: "text-[#94A3B8]", bg: "bg-[#94A3B8]/10" },
};

const INITIAL_FORM = { label: "", type: "wordpress", url: "", username: "", password: "", notes: "" };

export default function SecureVault({ clientId }) {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [revealedId, setRevealedId] = useState(null);
  const [decrypted, setDecrypted] = useState({});
  const [historyId, setHistoryId] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/vault`);
      setEntries(res.data || []);
    } catch {
      toast.error("Erreur chargement coffre-fort");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Label requis"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/vault/${editingId}`, form);
        toast.success("Entree mise a jour");
      } else {
        await axios.post(`${API}/clients/${clientId}/vault`, form);
        toast.success("Acces ajoute au coffre-fort");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      await fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (entry) => {
    setEditingId(entry.id);
    let pwd = "", notes = "";
    try {
      const res = await axios.get(`${API}/vault/${entry.id}/decrypt`);
      pwd = res.data.password || "";
      notes = res.data.notes || "";
    } catch { /* ignore */ }
    setForm({
      label: entry.label,
      type: entry.type,
      url: entry.url || "",
      username: entry.username || "",
      password: pwd,
      notes: notes,
    });
    setShowForm(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Supprimer cet acces ?")) return;
    try {
      await axios.delete(`${API}/vault/${entryId}`);
      toast.success("Entree supprimee");
      await fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleReveal = async (entryId) => {
    if (revealedId === entryId) {
      setRevealedId(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/vault/${entryId}/decrypt`);
      setDecrypted(prev => ({ ...prev, [entryId]: res.data }));
      setRevealedId(entryId);
    } catch {
      toast.error("Erreur dechiffrement");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copie dans le presse-papiers`);
  };

  const showHistory = async (entryId) => {
    if (historyId === entryId) { setHistoryId(null); return; }
    try {
      const res = await axios.get(`${API}/vault/${entryId}/history`);
      setHistory(res.data || []);
      setHistoryId(entryId);
    } catch {
      toast.error("Erreur chargement historique");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="secure-vault">
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#0099CC]" />
            Coffre-fort securise
            <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">AES-256</Badge>
          </h3>
          <Button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(INITIAL_FORM); }}
            size="sm"
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
            data-testid="add-vault-entry-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Ajouter un acces
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSave} className="bg-[#0B0F19] border border-[#2A3441] rounded-xl p-4 mb-4 space-y-3" data-testid="vault-entry-form">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white text-sm font-medium">{editingId ? "Modifier l'acces" : "Nouvel acces"}</h4>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="text-[#64748b] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Label *</Label>
                <Input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder="Ex: WordPress Production" className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="vault-label-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Type</Label>
                <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full bg-[#151B2B] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10 text-sm" data-testid="vault-type-select">
                  {Object.entries(TYPE_CONFIG).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">URL</Label>
                <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://..." className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="vault-url-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Identifiant</Label>
                <Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} placeholder="admin" className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="vault-username-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Mot de passe</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="********" className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="vault-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Instructions supplementaires" className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="vault-notes-input" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} variant="outline" className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#10B981] hover:bg-[#0D9668] text-white" data-testid="save-vault-entry-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                {editingId ? "Mettre a jour" : "Enregistrer"}
              </Button>
            </div>
          </form>
        )}

        {/* Entries */}
        {entries.length === 0 ? (
          <div className="text-center py-8">
            <Lock className="h-10 w-10 text-[#2A3441] mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">Aucun acces enregistre</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => {
              const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.autre;
              const Icon = cfg.icon;
              const isRevealed = revealedId === entry.id;
              const dec = decrypted[entry.id] || {};
              return (
                <div key={entry.id} data-testid={`vault-entry-${entry.id}`}>
                  <div className="bg-[#0B0F19] border border-[#2A3441] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`h-5 w-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-white font-medium text-sm">{entry.label}</p>
                          <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                        </div>
                        {entry.url && (
                          <a href={entry.url} target="_blank" rel="noopener noreferrer" className="text-[#0099CC] text-xs hover:underline">{entry.url}</a>
                        )}
                        {entry.username && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[#64748b] text-xs">ID:</span>
                            <span className="text-[#94A3B8] text-xs font-mono">{entry.username}</span>
                            <button onClick={() => copyToClipboard(entry.username, "Identifiant")} className="text-[#64748b] hover:text-[#0099CC]">
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {entry.has_password && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[#64748b] text-xs">MDP:</span>
                            <span className="text-[#94A3B8] text-xs font-mono">{isRevealed ? dec.password : "**********"}</span>
                            {isRevealed && dec.password && (
                              <button onClick={() => copyToClipboard(dec.password, "Mot de passe")} className="text-[#64748b] hover:text-[#0099CC]" data-testid={`copy-pwd-${entry.id}`}>
                                <Copy className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        )}
                        {isRevealed && dec.notes && (
                          <div className="mt-1">
                            <span className="text-[#64748b] text-xs">Notes: </span>
                            <span className="text-[#94A3B8] text-xs">{dec.notes}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {entry.has_password && (
                          <Button variant="ghost" size="icon" onClick={() => handleReveal(entry.id)} className="h-8 w-8 text-[#64748b] hover:text-white" data-testid={`reveal-pwd-${entry.id}`}>
                            {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => showHistory(entry.id)} className="h-8 w-8 text-[#64748b] hover:text-[#0099CC]" data-testid={`history-btn-${entry.id}`}>
                          <Clock className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)} className="h-8 w-8 text-[#64748b] hover:text-[#F59E0B]" data-testid={`edit-vault-${entry.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)} className="h-8 w-8 text-[#64748b] hover:text-[#EF4444]" data-testid={`delete-vault-${entry.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* History panel */}
                  {historyId === entry.id && history.length > 0 && (
                    <div className="mt-1 ml-12 bg-[#0B0F19]/50 border border-[#2A3441] rounded-lg p-3">
                      <p className="text-[#94A3B8] text-xs font-medium mb-2">Historique des modifications</p>
                      {history.map((h) => (
                        <div key={h.id} className="flex items-center gap-2 py-1">
                          <Clock className="h-3 w-3 text-[#64748b]" />
                          <span className="text-[#94A3B8] text-xs">{h.action}</span>
                          {h.fields_changed && <span className="text-[#64748b] text-xs">({h.fields_changed.join(", ")})</span>}
                          <span className="text-[#64748b] text-xs ml-auto">{new Date(h.created_at).toLocaleString("fr-FR")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
